import React, { memo, useMemo, useRef } from 'react';

import {
  Box,
  Code,
  Flex,
  Icon,
  Heading,
  Link,
  ListItem,
  OrderedList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList,
} from '@chakra-ui/react';
import { FiArrowRight, FiBookOpen, FiExternalLink } from 'react-icons/fi';
import Markdown, { Components } from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import { DocumentationChapter } from 'api/documentation';

// Code blocks live in their own file so the API reference can reuse them.
import { isModifiedClick, parseDocumentationHref } from './DocsPanel/utils';
import { DocumentationCodeBlock } from './DocumentationCodeBlock';
import { DocumentationImage, InlineImageContext } from './DocumentationImage';
import {
  appLinkHref,
  chapterPath,
  DOCUMENTATION_FONT_FAMILY,
  DOCUMENTATION_ROOT,
  DocumentationAnchor,
  headingIdsByLine,
  isAppRouteHref,
  keepSearch,
  markdownText,
  scrollToHeading,
  slugifyHeading,
} from './utils';

interface Props {
  anchorMap: Record<string, DocumentationAnchor>;
  chapter: DocumentationChapter;
  /**
   * Handles a plain click on an in-app link (the follow-along flow). Without
   * it app links open in a new tab; modified clicks always do.
   */
  onAppLink?: (href: string) => void;
  /** In the follow-along panel, docs links stay inside the panel. */
  onDocumentLink?: (path: string, anchor?: string) => void;
  /** The panel is narrower, so its headings are smaller. */
  variant?: 'page' | 'panel';
}

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const REMARK_PLUGINS = [remarkGfm];

/** A paragraph whose only content is one in-app link renders as a CTA. */
const callToActionHref = (node: any): string | undefined => {
  const children = (node?.children ?? []).filter(
    (child: any) => !(child.type === 'text' && !child.value.trim()),
  );
  const [only] = children;
  if (children.length !== 1 || only.type !== 'element' || only.tagName !== 'a')
    return undefined;
  const href = only.properties?.href;
  return typeof href === 'string' && isAppRouteHref(href) ? href : undefined;
};

/** An image that sits in a sentence (a UI icon) renders inline with the text. */
const hasInlineImage = (node: any): boolean => {
  const children = node?.children ?? [];
  return (
    children.some((child: any) => child.tagName === 'img') &&
    children.some(
      (child: any) =>
        (child.type === 'text' && child.value.trim()) ||
        (child.type === 'element' && child.tagName !== 'img'),
    )
  );
};

/** The plain text of a hast node, e.g. the code inside a <pre>. */
const hastText = (node: any): string =>
  node?.type === 'text'
    ? node.value
    : (node?.children ?? []).map(hastText).join('');

/** The signed-in organization, which the app keeps per tab. */
const currentOrganization = (): string => {
  try {
    return sessionStorage.getItem('realm') ?? '';
  } catch {
    return '';
  }
};

/**
 * In-app links. A plain click goes to `onClick` (follow along, same tab);
 * the href still carries the organization and opens a new tab for modified
 * clicks, or for every click where nothing handles it.
 */
const appLinkProps = (href: string, onClick?: (href: string) => void) => ({
  href: appLinkHref(href, window.location.search, currentOrganization()),
  target: '_blank',
  rel: 'noopener noreferrer',
  title: onClick ? 'Opens beside this guide' : 'Opens in a new tab',
  onClick: (event: React.MouseEvent) => {
    if (!onClick || isModifiedClick(event)) return;
    event.preventDefault();
    onClick(href);
  },
});

/**
 * Renders a chapter's Markdown. The component map must keep its identity
 * across renders: react-markdown uses each entry as a component type, so a
 * new map would remount every heading and image, collapsing images back to
 * placeholders and throwing off scrolling to a section.
 */
export const DocumentationMarkdown = memo<Props>(
  ({ anchorMap, chapter, onAppLink, onDocumentLink, variant = 'page' }) => {
    // useNavigate changes identity with the location; read the latest via a ref.
    const latestNavigate = useNavigate();
    const navigateRef = useRef(latestNavigate);
    navigateRef.current = latestNavigate;
    // Link handlers come from parents that re-render often; refs keep the
    // component map (and so every heading and image) from remounting.
    const onAppLinkRef = useRef(onAppLink);
    onAppLinkRef.current = onAppLink;
    const onDocumentLinkRef = useRef(onDocumentLink);
    onDocumentLinkRef.current = onDocumentLink;
    const hasAppLinkHandler = !!onAppLink;

    const components = useMemo(() => {
      const navigate = (to: string) =>
        navigateRef.current(keepSearch(to, window.location.search));
      const handleAppLink = hasAppLinkHandler
        ? (href: string) => onAppLinkRef.current?.(href)
        : undefined;
      /** Opens a docs page: inside the panel there, else on the docs page. */
      const openDocument = (path: string, anchor?: string) => {
        if (onDocumentLinkRef.current)
          return onDocumentLinkRef.current(path, anchor);
        if (path === chapterPath(chapter) && anchor) scrollToHeading(anchor);
        navigate(`${DOCUMENTATION_ROOT}/${path}${anchor ? `#${anchor}` : ''}`);
      };
      const headingIds = headingIdsByLine(chapter);
      const chapterHeadingIds = new Set(
        (chapter.headings ?? []).map(item => item.id),
      );

      const heading = (
        level: HeadingLevel,
        children: React.ReactNode,
        node?: any,
      ) => {
        // Bold black headings; purple is kept for links so headings never read
        // as clickable.
        const sizes =
          variant === 'panel'
            ? {
                1: '24px',
                2: '21px',
                3: '18px',
                4: '16px',
                5: '16px',
                6: '16px',
              }
            : {
                1: '30px',
                2: '27px',
                3: '21px',
                4: '18px',
                5: '17px',
                6: '17px',
              };
        // Each top-level section starts with a rule for easier scanning.
        const sectionRule =
          level === 2
            ? { borderTop: '1px solid', borderColor: 'gray.100', pt: 8 }
            : {};
        return (
          <Heading
            as={`h${level}` as any}
            id={
              headingIds.get(node?.position?.start?.line) ??
              slugifyHeading(markdownText(children))
            }
            fontFamily={DOCUMENTATION_FONT_FAMILY}
            fontSize={sizes[level]}
            fontWeight={600}
            lineHeight={1.3}
            color="black"
            mb={level > 3 ? 2 : 3}
            mt={level === 1 ? 0 : level === 2 ? 12 : level === 3 ? 8 : 6}
            // Clears the app's sticky header (set by the docs page).
            scrollMarginTop="calc(var(--docs-header-height, 0px) + 16px)"
            {...sectionRule}
          >
            {children}
          </Heading>
        );
      };

      const linkStyle = {
        color: 'primary',
        fontWeight: 400,
        textDecoration: 'underline',
        textDecorationThickness: '1px',
        textUnderlineOffset: '3px',
        _hover: { textDecorationThickness: '2px' },
      };

      const isExternalHref = (href: string) =>
        /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(href);

      const normalizeInternalHref = (href: string) => {
        if (href.startsWith('/')) return href;
        const cleaned = href.replace(/^\.?\//, '');
        return cleaned.startsWith('documentation/')
          ? `/${cleaned}`
          : `/documentation/${cleaned}`;
      };

      const map: Components = {
        h1: ({ node, children }) => heading(1, children, node),
        h2: ({ node, children }) => heading(2, children, node),
        h3: ({ node, children }) => heading(3, children, node),
        h4: ({ node, children }) => heading(4, children, node),
        h5: ({ node, children }) => heading(5, children, node),
        h6: ({ node, children }) => heading(6, children, node),
        p: ({ node, children }) => {
          const ctaHref = callToActionHref(node);
          if (ctaHref) {
            // Word styles these as purple text with an arrow, not buttons.
            return (
              <Box mb={4}>
                <Link
                  {...appLinkProps(ctaHref, handleAppLink)}
                  display="inline-flex"
                  alignItems="center"
                  gap={1.5}
                  color="primary"
                  fontWeight={400}
                >
                  {markdownText(children)}
                  {/* An arrow when it opens beside the guide, else new tab. */}
                  <Icon as={handleAppLink ? FiArrowRight : FiExternalLink} />
                </Link>
              </Box>
            );
          }
          return (
            <InlineImageContext.Provider value={hasInlineImage(node)}>
              <Text mb={4} fontSize="16px" lineHeight={1.75} color="gray.800">
                {children}
              </Text>
            </InlineImageContext.Provider>
          );
        },
        img: ({ src, alt }) => (
          <DocumentationImage src={src as string | undefined} alt={alt} />
        ),
        a: ({ href = '', children }) => {
          if (href.startsWith('#')) {
            const anchor = href.slice(1);
            const destination = chapterHeadingIds.has(anchor)
              ? { chapterPath: chapterPath(chapter), anchor }
              : anchorMap[anchor];
            return (
              <Link
                href={href}
                {...linkStyle}
                onClick={event => {
                  if (!destination) return;
                  event.preventDefault();
                  openDocument(destination.chapterPath, destination.anchor);
                }}
              >
                {children}
              </Link>
            );
          }
          if (!isExternalHref(href)) {
            const target = normalizeInternalHref(href);
            if (isAppRouteHref(target))
              return (
                <Link {...appLinkProps(target, handleAppLink)} {...linkStyle}>
                  {children}
                  {!handleAppLink && (
                    <Icon as={FiExternalLink} boxSize="0.8em" ml={1} />
                  )}
                </Link>
              );
            const documentTarget = parseDocumentationHref(target);
            return (
              <Link
                href={target}
                {...linkStyle}
                onClick={event => {
                  if (isModifiedClick(event)) return;
                  event.preventDefault();
                  if (documentTarget)
                    openDocument(documentTarget.path, documentTarget.anchor);
                  else navigate(target);
                }}
              >
                {children}
              </Link>
            );
          }
          return (
            <Link
              href={href}
              {...linkStyle}
              isExternal
              rel="noopener noreferrer"
            >
              {children}
            </Link>
          );
        },
        // Notes and tips: the tinted, rounded box with a book icon from Word.
        blockquote: ({ children }) => (
          <Flex
            gap={3}
            align="flex-start"
            bg="selectedSideNavMenuBg"
            px={4}
            pt={3}
            pb={0.5}
            mb={5}
            borderRadius={10}
            sx={{ '& p': { mb: 2.5 } }}
          >
            <Icon as={FiBookOpen} color="primary" boxSize={5} mt={1} />
            <Box flex={1}>{children}</Box>
          </Flex>
        ),
        ul: ({ children }) => (
          <UnorderedList
            mb={4}
            pl={5}
            spacing={1.5}
            sx={{ '& li > p': { mb: 1 }, '& li > p:last-child': { mb: 0 } }}
          >
            {children}
          </UnorderedList>
        ),
        ol: ({ children }) => (
          <OrderedList
            mb={4}
            pl={5}
            spacing={1.5}
            sx={{ '& li > p': { mb: 1 }, '& li > p:last-child': { mb: 0 } }}
          >
            {children}
          </OrderedList>
        ),
        li: ({ children }) => (
          <ListItem fontSize="16px" lineHeight={1.7} color="gray.800">
            {children}
          </ListItem>
        ),
        pre: ({ node }) => (
          <DocumentationCodeBlock code={hastText(node).replace(/\n$/, '')} />
        ),
        code: ({ children }) => (
          <Code colorScheme="purple" borderRadius="md" px={1.5} py={0.5}>
            {children}
          </Code>
        ),
        table: ({ children }) => (
          <TableContainer mb={6} border="1px solid" borderColor="gray.200">
            <Table variant="simple" size="sm">
              {children}
            </Table>
          </TableContainer>
        ),
        thead: ({ children }) => <Thead bg="gray.50">{children}</Thead>,
        tbody: ({ children }) => <Tbody>{children}</Tbody>,
        tr: ({ children }) => <Tr>{children}</Tr>,
        th: ({ children }) => <Th whiteSpace="normal">{children}</Th>,
        td: ({ children }) => (
          <Td whiteSpace="normal" verticalAlign="top">
            {children}
          </Td>
        ),
      };
      return map;
    }, [anchorMap, chapter, hasAppLinkHandler, variant]);

    return (
      <Markdown remarkPlugins={REMARK_PLUGINS} components={components}>
        {chapter.markdown}
      </Markdown>
    );
  },
);
